import React, { FunctionComponent, useEffect, useState } from 'react';
import Button from 'components/button/Button';
import InputField from 'components/textField/InputField';
import { Link, useHistory, useParams } from 'react-router-dom';
import Styles from 'views/register/Register.module.scss';
import api from 'store/services/auth.services';
import LoginLeftSection from 'components/loginLeftSection/LoginLeftSection';
import { trackFormInteraction, trackEvent, ClarityEvents } from 'utils/clarity';

type ResetParams = {
  uidb64: string;
  token: string;
};

const parseApiError = (err: any): string => {
  try {
    const data = JSON.parse(err.message);
    if (data.message) return data.message;
    if (data.error) return typeof data.error === 'string' ? data.error : 'Invalid request';
  } catch {
    // fall through
  }
  return err?.message || 'Something went wrong';
};

const ResetPassword: FunctionComponent = () => {
  const { uidb64, token } = useParams<ResetParams>();
  const history = useHistory();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [linkExpired, setLinkExpired] = useState(false);
  const isSubmittedRef = React.useRef(false);

  useEffect(() => {
    trackFormInteraction('reset_password', 'opened');
    return () => {
      if (!isSubmittedRef.current) {
        trackFormInteraction('reset_password', 'cancelled');
      }
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const validate = async () => {
      if (!uidb64 || !token) {
        setLinkExpired(true);
        setValidating(false);
        return;
      }
      try {
        await api.validateResetToken(uidb64, token);
        if (!cancelled) {
          setLinkExpired(false);
          setValidating(false);
        }
      } catch (err: any) {
        if (!cancelled) {
          setLinkExpired(true);
          setValidating(false);
          trackEvent(ClarityEvents.API_FAILURE, {
            type: 'reset_password_validate_error',
            message: parseApiError(err),
          });
        }
      }
    };
    validate();
    return () => {
      cancelled = true;
    };
  }, [uidb64, token]);

  const checkConfirmPassword = (confirmVal?: string) => {
    const confirm = confirmVal !== undefined ? confirmVal : confirmPassword;
    if (password.length === 0 && confirm.length === 0) return '';
    if (password.length > 0 && password.length < 6) return 'Password must be 6 characters';
    if (confirm.length > 0 && password !== confirm) return 'Passwords do not match';
    return '';
  };

  const handleBlur = () => {
    setPasswordError(checkConfirmPassword());
  };

  const handleRequest = async () => {
    const validationError = checkConfirmPassword();
    setPasswordError(validationError);
    if (validationError || password.length === 0 || confirmPassword.length === 0) {
      trackFormInteraction('reset_password', 'validation_error', validationError || 'empty_fields');
      return;
    }
    try {
      setIsLoading(true);
      setError('');
      await api.resetPassword({
        uidb64,
        token,
        password,
        password1: confirmPassword,
      });
      isSubmittedRef.current = true;
      trackFormInteraction('reset_password', 'submitted');
      setIsLoading(false);
      history.push('/login?reset=1');
    } catch (err: any) {
      setIsLoading(false);
      const errMsg = parseApiError(err);
      setError(errMsg);
      if (errMsg.toLowerCase().includes('expired')) {
        setLinkExpired(true);
      }
      trackEvent(ClarityEvents.API_FAILURE, { type: 'reset_password_error', message: errMsg });
    }
  };

  const handleKeyDownEvent = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      handleRequest();
    }
  };

  return (
    <div className={Styles.customContainer}>
      <div className={Styles.leftSectionWrapper}>
        <LoginLeftSection />
      </div>
      <div className={Styles.rightSectionWrapper}>
        <div className={Styles.loginArea}>
          <h4 className={Styles.accountHeading}>Reset your password</h4>

          {validating && <p className="fontFifteen text-black">Checking your reset link…</p>}

          {!validating && linkExpired && (
            <>
              <p className={Styles.error}>This reset link has expired or is invalid.</p>
              <div className={`text-center ${Styles.signUpMarginBLock}`}>
                <Button label="Request a new link" clickHandler={() => history.push('/forgot-password')} />
              </div>
            </>
          )}

          {!validating && !linkExpired && (
            <>
              <div className={`mb-0 ${Styles.formWrapper}`}>
                <div className="form-group mb-3">
                  <InputField handleChange={setPassword} type="password" placeholder="New Password" />
                </div>
                <div className="form-group mb-1">
                  <InputField
                    handleKeyDown={handleKeyDownEvent}
                    blurHandler={handleBlur}
                    handleChange={setConfirmPassword}
                    type="password"
                    placeholder="Confirm Password"
                  />
                  {passwordError && <p className={Styles.error}>{passwordError}</p>}
                  {error && <p className={Styles.error}>{error}</p>}
                </div>
              </div>
              <div className={`text-center ${Styles.signUpMarginBLock}`}>
                <Button showLoading={isLoading} clickHandler={handleRequest} label="Update Password" />
              </div>
            </>
          )}

          <p className="fontFifteen font-weight-normal text-black">
            Remember your password?&nbsp;
            <Link
              to="/login"
              className="text-blue text-decoration-none display-inline-block fontFifteen font-weight-bolder"
            >
              Login
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
